import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Grid,
  GridItem,
  VStack,
  HStack,
  Select,
  Input,
  Box,
  Image,
  Badge,
  FormControl,
  FormLabel,
  Text,
  useToast,
  Flex,
} from "@chakra-ui/react";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface Address {
  street: string;
  city: string;
  state: string;
  zip: string;
}

interface Contact {
  phone: string;
  email: string;
}

interface ImageData {
  url: string;
  _id: string;
}

interface LocationPoint {
  type: string;
  coordinates: number[];
}

interface OriginalData {
  _id: string;
  name: string;
  about: string;
  address: Address;
  contact: Contact;
  bookingFeePerPerson: {
    lunch: number;
    dinner: number;
  };
  cuisines: string[];
  features: string[];
  crowd: string[];
  serveTime: string[];
  image: ImageData;
  locationPoint: LocationPoint;
  popularDishes: any;
  termsAndCondition: any;
}

interface RestaurantData {
  key: string;
  index: number;
  name: string;
  address: string;
  phoneNumber: string;
  email: string;
  cuisineType: string;
  image: string;
  originalData: OriginalData;
}

interface RestaurantDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantData?: RestaurantData;
}

interface GalleryImage {
  _id: string;
  url: string;
}

const RestaurantDetailsModal: React.FC<RestaurantDetailsModalProps> = ({
  isOpen,
  onClose,
  restaurantData,
}) => {
  const toast = useToast();
  const [galleryImages, setGalleryImages] = useState<string[]>([]);
  const [menuImages, setMenuImages] = useState<string[]>([]);
  const modules = {
    toolbar: false,
  };
  useEffect(() => {
    if (restaurantData?.originalData?._id) {
      fetchGalleryImages();
      fetchMenuImages();
    }
  }, [restaurantData?.originalData?._id]);

  const fetchGalleryImages = async () => {
    if (!restaurantData?.originalData?._id) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/gallery/${restaurantData.originalData._id}`
      );
      let images = [];
      if (Array.isArray(response.data.data)) {
        images = response.data.data?.[0]?.images || [];
      } else {
        images = response.data.data?.images || [];
      }
      // const images = response.data.data || [];
      const previews = images
        .map((img: GalleryImage) =>
          img?.url ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${img.url}` : ""
        )
        .filter(Boolean);
      setGalleryImages(previews);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load gallery images",
        status: "error",
        duration: 3000,
      });
    }
  };

  const fetchMenuImages = async () => {
    if (!restaurantData?.originalData?._id) return;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/admin/menu/${restaurantData.originalData._id}`
      );
      let images = [];
      if (Array.isArray(response.data.data)) {
        images = response.data.data?.[0]?.images || [];
      } else {
        images = response.data.data?.images || [];
      }
      // const images = response.data.data || [];
      const previews = images
        .map((img: GalleryImage) =>
          img?.url ? `${process.env.REACT_APP_IMAGE_BASE_URL}/${img.url}` : ""
        )
        .filter(Boolean);
      setMenuImages(previews);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load menu images",
        status: "error",
        duration: 3000,
      });
    }
  };

  if (!restaurantData) return null;

  const { originalData } = restaurantData;
  const fullAddress = `${originalData.address.street}, ${originalData.address.city}, ${originalData.address.state} ${originalData.address.zip}`;
  const [longitude, latitude] = originalData.locationPoint?.coordinates || [
    0, 0,
  ];
  console.log("originalData", originalData);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="900px" borderRadius={"10px"}>
        <ModalHeader
          py={4}
          bg="#e244411a"
          border={"1px solid #E0E0E0"}
          borderTopLeftRadius={"10px"}
          borderTopRightRadius={"10px"}
          // alignItems={"stretch"}
          // flex="0 0 auto"
        >
          <Text fontSize="lg">Restaurant Details</Text>
          <ModalCloseButton />
        </ModalHeader>

        <ModalBody
          py={6}
          maxHeight={"80vh"}
          // flex="1 1 auto"
          overflowY="auto"
          css={{
            "&::-webkit-scrollbar": {
              width: "4px",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
              borderRadius: "2px",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          <VStack spacing={6} align="stretch">
            <Grid templateColumns="repeat(2, 1fr)" gap={8}>
              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Gallery</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {galleryImages.length === 0 ? (
                      <Text>No gallery image uploaded</Text>
                    ) : (
                      galleryImages.map((img, index) => (
                        <Box
                          key={index}
                          w="80px"
                          h="80px"
                          borderRadius="6px"
                          overflow="hidden"
                        >
                          <Image
                            src={img}
                            alt={`Gallery ${index + 1}`}
                            w="full"
                            h="full"
                            objectFit="cover"
                          />
                        </Box>
                      ))
                    )}
                  </Flex>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Menu</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {menuImages.length === 0 ? (
                      <Text>No Menu image uploaded</Text>
                    ) : (
                      menuImages.map((img, index) => (
                        <Box
                          key={index}
                          w="80px"
                          h="80px"
                          borderRadius="6px"
                          overflow="hidden"
                        >
                          <Image
                            src={img}
                            alt={`Menu ${index + 1}`}
                            w="full"
                            h="full"
                            objectFit="cover"
                          />
                        </Box>
                      ))
                    )}
                  </Flex>
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns="repeat(2, 1fr)" gap={6}>
              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Restaurant Image</FormLabel>
                  <Box w="200px" h="150px" borderRadius="6px" overflow="hidden">
                    <Image
                      src={restaurantData?.image}
                      alt={originalData.name}
                      w="full"
                      h="full"
                      objectFit="cover"
                    />
                  </Box>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">About</FormLabel>
                  <Text>{originalData?.about}</Text>
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Restaurant Name</FormLabel>
                  <Input
                    value={originalData?.name}
                    isReadOnly
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Restaurant Address</FormLabel>
                  <Input
                    value={fullAddress}
                    isReadOnly
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Phone Number</FormLabel>
                  <Input
                    value={originalData?.contact.phone}
                    isReadOnly
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Email</FormLabel>
                  <Input
                    value={originalData?.contact.email}
                    isReadOnly
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                  />
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Coordinates</FormLabel>
                  <VStack align="stretch" spacing={2}>
                    <Input
                      value={`Longitude: ${longitude}`}
                      isReadOnly
                      bg="white"
                      border="1px"
                      borderColor="gray.200"
                    />
                    <Input
                      value={`LLatitude: ${latitude}`}
                      isReadOnly
                      bg="white"
                      border="1px"
                      borderColor="gray.200"
                    />
                  </VStack>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Booking Fee Per Person</FormLabel>
                  <VStack align="stretch" spacing={2}>
                    <Input
                      value={`Lunch: $${originalData?.bookingFeePerPerson.lunch}`}
                      isReadOnly
                      bg="white"
                      border="1px"
                      borderColor="gray.200"
                    />
                    <Input
                      value={`Dinner: $${originalData?.bookingFeePerPerson.dinner}`}
                      isReadOnly
                      bg="white"
                      border="1px"
                      borderColor="gray.200"
                    />
                  </VStack>
                </FormControl>
              </GridItem>
            </Grid>

            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Features</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {originalData?.features.map((feature, index) => (
                      <Badge
                        key={index}
                        variant="solid"
                        px={4}
                        py={2}
                        borderRadius="5px"
                        bg="#FFF0F0"
                        color="#E24441"
                        fontWeight="normal"
                      >
                        {feature}
                      </Badge>
                    ))}
                  </Flex>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Crowd</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {originalData?.crowd.map((item, index) => (
                      <Badge
                        key={index}
                        variant="solid"
                        px={4}
                        py={2}
                        borderRadius="5px"
                        bg="#FFF0F0"
                        color="#E24441"
                        fontWeight="normal"
                      >
                        {item}
                      </Badge>
                    ))}
                  </Flex>
                </FormControl>
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Cuisines</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {originalData?.cuisines.map((cuisine, index) => (
                      <Badge
                        key={index}
                        variant="solid"
                        px={4}
                        py={2}
                        borderRadius="5px"
                        bg="#FFF0F0"
                        color="#E24441"
                        fontWeight="normal"
                      >
                        {cuisine}
                      </Badge>
                    ))}
                  </Flex>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Serve Time</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {originalData?.serveTime.map((time, index) => (
                      <Badge
                        key={index}
                        variant="solid"
                        px={4}
                        py={2}
                        borderRadius="5px"
                        bg="#FFF0F0"
                        color="#E24441"
                        fontWeight="normal"
                      >
                        {time}
                      </Badge>
                    ))}
                  </Flex>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel color="gray.600">Popular Dishes</FormLabel>
                  <Flex wrap="wrap" gap={2}>
                    {originalData?.popularDishes &&
                    originalData?.popularDishes.length > 0 ? (
                      originalData.popularDishes.map(
                        (dish: any, index: any) => (
                          <Badge
                            key={index}
                            variant="solid"
                            px={4}
                            py={2}
                            borderRadius="5px"
                            bg="#FFF0F0"
                            color="#E24441"
                            fontWeight="normal"
                          >
                            {dish.name} ({dish.type})
                          </Badge>
                        )
                      )
                    ) : (
                      <Text>No popular dishes found</Text>
                    )}
                  </Flex>
                </FormControl>
              </GridItem>
            </Grid>
            <Box>
              <FormControl>
                <FormLabel color="gray.600">Special Terms & Conditions</FormLabel>
                {/* <Input
                    value={originalData?.termsAndCondition}
                    isReadOnly
                    bg="white"
                    border="1px"
                    borderColor="gray.200"
                  /> */}
                <ReactQuill
                  value={originalData?.termsAndCondition}
                  readOnly={true}
                  modules={modules}
                  theme="snow"
                />
              </FormControl>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default RestaurantDetailsModal;
